import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    obterCuponsEstabelecimento,
    salvarCuponsEstabelecimento,
} from "../../../services/cupomEstabelecimento"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { CupomEstabelecimentoFornecedorInicial } from "../models/cupomEstabelecimentoFornecedorModel"
import { CupomEstabelecimentoFornecedorActionsTypes } from "./cupomEstabelecimentoFornecedorActions"
import { CupomEstabelecimentoFornecedorContext } from "./CupomEstabelecimentoFornecedorContext"
import { cupomEstabelecimentoFornecedorReducer } from "./cupomEstabelecimentoFornecedorReducer"

export function CupomEstabelecimentoFornecedorContextProvider({
    children,
}: {
    children: React.ReactNode
}) {
    const [dados, dispatch] = useReducer(
        cupomEstabelecimentoFornecedorReducer,
        CupomEstabelecimentoFornecedorInicial
    )
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await obterCuponsEstabelecimento(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar cupons")
            } else {
                setErroCarregamento(null)
                const itens = response.map((c, i) => ({
                    localId: i + 1,
                    id: c.id,
                    cupomId: c.cupomId,
                    nomeCupom: c.nomeCupom,
                    valor: c.valor,
                    percentual: c.percentual,
                    quantidadePorCliente: c.quantidadePorCliente,
                    quantidadeMaxima: c.quantidadeMaxima,
                    valorMinimoPedido: c.valorMinimoPedido,
                    maximoDesconto: c.maximoDesconto,
                    apenasPrimeiraCompra: c.apenasPrimeiraCompra,
                    automatico: c.automatico,
                    ativo: c.ativo,
                    freteGratis: c.freteGratis,
                    appSiteTodos: c.appSiteTodos,
                    validadeDias: c.validadeDias,
                    fimCupomTimestamp: c.fimCupomTimestamp,
                    visivelTelaInicial: c.visivelTelaInicial,
                    horarioAlmoco: c.horarioAlmoco,
                    exigirFacebookParaDesconto: c.exigirFacebookParaDesconto,
                    exigirEnderecoUnicoParaDesconto: c.exigirEnderecoUnicoParaDesconto,
                    exigirCpfParaDesconto: c.exigirCpfParaDesconto,
                    telefoneCliente: c.telefoneCliente,
                    descricao: c.descricao ?? "",
                    olharListaClientes: c.olharListaClientes,
                    quantidadeUtilizada: c.quantidadeUtilizada,
                }))
                dispatch({
                    type: CupomEstabelecimentoFornecedorActionsTypes.CARREGAR,
                    payload: itens,
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar cupons")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
    }, [])

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const payload = {
                cupons: dados.cupons.map(c => ({
                    id: c.id,
                    cupomId: c.cupomId,
                    nomeCupom: c.nomeCupom,
                    valor: c.valor,
                    percentual: c.percentual,
                    quantidadePorCliente: c.quantidadePorCliente,
                    quantidadeMaxima: c.quantidadeMaxima,
                    valorMinimoPedido: c.valorMinimoPedido,
                    maximoDesconto: c.maximoDesconto,
                    apenasPrimeiraCompra: c.apenasPrimeiraCompra,
                    automatico: c.automatico,
                    ativo: c.ativo,
                    freteGratis: c.freteGratis,
                    appSiteTodos: c.appSiteTodos,
                    validadeDias: c.validadeDias,
                    fimCupomTimestamp: c.fimCupomTimestamp,
                    visivelTelaInicial: c.visivelTelaInicial,
                    horarioAlmoco: c.horarioAlmoco,
                    exigirFacebookParaDesconto: c.exigirFacebookParaDesconto,
                    exigirEnderecoUnicoParaDesconto: c.exigirEnderecoUnicoParaDesconto,
                    exigirCpfParaDesconto: c.exigirCpfParaDesconto,
                    telefoneCliente: c.telefoneCliente,
                    descricao: c.descricao,
                    olharListaClientes: c.olharListaClientes,
                })),
                deletarIds: dados.pendingDelete,
            }
            const response = await salvarCuponsEstabelecimento(autorizacao, payload)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({
                type: CupomEstabelecimentoFornecedorActionsTypes.CONFIRMAR_SAVE,
                payload: response,
            })
        } finally {
            setLoading(false)
        }
    }, [dados])

    return (
        <CupomEstabelecimentoFornecedorContext.Provider
            value={{ dados, dispatch, salvar, erroCarregamento }}
        >
            <Loading />
            {children}
        </CupomEstabelecimentoFornecedorContext.Provider>
    )
}
